/**
 * Open-document session for yap — the thin multi-document layer over the
 * app's own document store (`state/useDocumentStore`).
 *
 * yap's workbenches stay a single-ACTIVE-document machine; this store keeps
 * the other open documents as descriptors so the shared document workspace
 * (`@suite/documents`) can present them as tabs. File-backed documents keep
 * a recents entry and reopen through the existing `openRecentDocument`
 * pipeline; a document that leaves the active slot with unsaved (or
 * disk-unresolvable) content parks an in-memory envelope snapshot instead,
 * so switching tabs never discards work.
 */

import { create } from 'zustand';
import type { DocumentKind, RecentFile } from '../state/useDocumentStore';

/**
 * Parked in-memory state of a background document: the full document
 * envelope OBJECT (never a serialized string). Sharing live object
 * references is safe by invariant: a snapshot exists only while its document
 * is background (syncActive wipes it on activation), and a background
 * document's kernel and session-owned stores are replaced wholesale before
 * anything else can mutate them — so the parked graph stays frozen without
 * a deep copy, and tab switching pays zero stringify/parse.
 */
export interface SessionSnapshot {
    format: 'yap-project' | 'yap-template';
    version: number;
    appName: string;
    savedAt: string;
    project: Record<string, unknown>;
    cadScene?: unknown;
    sketchScene?: unknown;
}

export interface SessionDocument {
    key: string;
    name: string;
    kind: DocumentKind;
    /** Recents entry resolved by (name, kind) — the reopen path. Undefined
        for a brand-new unsaved document that has never been saved. */
    recent?: RecentFile;
    dirty: boolean;
    /** In-memory envelope parked when the document left the active slot with
        unsaved (or disk-unresolvable) content — the freshest reopen path for
        background tabs. Session-only by design; wiped when the document
        becomes active again (syncActive rebuilds its entry). */
    snapshot?: SessionSnapshot | string;
    /** Dirty flag captured at park time — a restored snapshot must keep the
     *  tab's dirty dot and save prompts truthful. */
    snapshotDirty?: boolean;
}

interface SessionState {
  docs: Record<string, SessionDocument>;
  activeKey: string | null;

  /** Called whenever the app's document store changes; opens/follows the
   * active document's session entry and refreshes its reopen descriptor. */
  syncActive: (doc: {
    key: string;
    name: string;
    kind: DocumentKind;
    dirty: boolean;
    recent?: RecentFile;
  }) => void;
  close: (key: string) => void;
  /** Drop entries that no longer resolve to a recents entry (Save As rename) */
  rekey: (oldKey: string, doc: SessionDocument) => void;
  /** Attach a parked in-memory envelope to a background (or about-to-go-
   * background) document — the live state snapshot taken just before the
   * kernel is handed to another document. */
  park: (key: string, snapshot: SessionSnapshot | string, snapshotDirty: boolean) => void;
}

export const useDocSession = create<SessionState>((set, get) => ({
  docs: {},
  activeKey: null,

  syncActive: (doc) => {
    const state = get();
    const existing = state.docs[doc.key];
    const next: SessionDocument = {
      key: doc.key,
      name: doc.name,
      kind: doc.kind,
      recent: doc.recent ?? existing?.recent,
      dirty: doc.dirty,
    };
    const docs = { ...state.docs };
    if (state.activeKey && state.activeKey !== doc.key && docs[state.activeKey]) {
      const previous = docs[state.activeKey];
      // Same underlying document renamed (Save As) — retire the old slot
      if (previous.name === doc.name && previous.kind === doc.kind) {
        delete docs[state.activeKey];
      }
    }
    docs[doc.key] = next;
    set({ docs, activeKey: doc.key });
  },

  close: (key) => {
    set((prev) => {
      if (!prev.docs[key]) return {};
      const docs = { ...prev.docs };
      delete docs[key];
      return {
        docs,
        activeKey: prev.activeKey === key ? null : prev.activeKey,
      };
    });
  },

  rekey: (oldKey, doc) => {
    set((prev) => {
      if (!prev.docs[oldKey]) return {};
      const docs = { ...prev.docs };
      delete docs[oldKey];
      docs[doc.key] = doc;
      return { docs };
    });
  },

  park: (key, snapshot, snapshotDirty) => {
    set((prev) => {
      if (!prev.docs[key]) return {};
      return {
        docs: { ...prev.docs, [key]: { ...prev.docs[key], snapshot, snapshotDirty } },
      };
    });
  },
}));
